/* eslint-disable max-lines */
/* tslint:disable:max-file-line-count */
/* eslint-disable camelcase */

import { comparer, isArrayLike } from 'mobx';

import { ILocalization } from 'bernie-l10n';
import { merge, SerializedData } from 'bernie-core';

import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';

import { PackageType } from 'models/multi-item/multi-item-types';

import { multiItemCarSearchCriteria, multiItemFlightSearchCriteria } from 'components/multi-item/utils';

import { PropertySort, SearchCriteriaModel } from './search-criteria-model';
import { RoomModel } from './room-model';

export interface UrlParamQueryIdMapper {
  URL_PARAM_ID: string;
  QUERY_BOOLEAN_ID: string;
}

export const URL_PARAM_TO_QUERY_ID_MAPPER = [
  {
    URL_PARAM_ID: 'packageRates',
    QUERY_BOOLEAN_ID: 'showPackageRates',
  },
];

export const untrackedQueryParams = [
  '0',
  '1',
  '2',
  'authSuccessMsg',
  'hotelId',
  'lang',
  'prefix',
  'pwaDialog',
  'pwaDialogNested',
  'pwaOverlay',
  'scrollPosition',
  'splat',
  'trv_curr',
  'trv_dp',
  'exp_dp',
  'exp_tx',
  'trv_bp',
  'propertyUnitRoomInfoDialog',
];
export const singleUseQueryParams = ['rfrr', 'top_dp', 'top_cur', 'trv_dp', 'trv_curr', 'authSuccessMsg', 'exp_dp', 'exp_tx', 'trv_bp'];
export const getSearchImpactingKeys = () => [
  'startDate',
  'endDate',
  'regionId',
  'selected',
  'latLong',
  'destination',
  'rooms',
  'sort',
  'hotelName',
  'price',
  'star',
  'lodging',
  'amenities',
  'travelerType',
  'poi',
  'useRewards', // Exp 29018 PWA_SwP_Toggle_with_more_than_1_in_points
  'guestRating',
  'accessibility',
  'paymentType',
  'bedType',
  'cleaningAndSafetyPractices',
  'bedroomFilter',
  'deals', // Deals Filter
  'propertyStyle',
  'rewards',
  'commissionTier',
  'agencyBusinessModel',
  'mealPlan',
  'neighborhood',
  'stayType',
  'bedroom_count_gt',
  'us_bathroom_count_gt',
  'property_type_group',
  'location_group',
  'accessibility_features_group',
  'booking_options_group',
  'discounts_group',
  'amenities_facilities_group',
  'free_cancellation_group',
  'house_rules_group',
  'nearby_activities_group',
  'neighborhood_group',
  'premier_host_group',
  'properties_good_for_group',
  'property_reviews_group',
  'rooms_spaces_group',
  'safety_group',
  'virtual_tour_group',
  'nightly_price',
  'total_price',
];

export const getMultiItemMutationImpactingKeys = (packageType: PackageType) => {
  const keys: string[] = ['startDate', 'endDate', 'destination', 'rooms'];

  if (packageType && packageType.includes('F')) {
    keys.push(...multiItemFlightSearchCriteria.filter(key => key !== 'airlineCode'));
  }

  if (packageType && packageType === PackageType.HC) {
    keys.push(...multiItemCarSearchCriteria);
  }

  return keys;
};

const getToday = (): string => new Date().toISOString().split('T')[0];

const getTomorrow = (): string => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return tomorrow.toISOString().split('T')[0];
};

const parseInfositeValues = (query: any, l10n: ILocalization, localDateFormat: string): SerializedData => {
  const iqaParsedInQuery = InfositeQueryAdapter.parseIn(query);
  const { adults, children, endDate, startDate } = SearchQueryAdapter.parseOut(iqaParsedInQuery);

  return {
    ...iqaParsedInQuery,
    adults,
    children,
    endDate: sanitizeDateToISO(endDate, l10n, localDateFormat),
    rooms: setDefaultRoomsIfNecessary(iqaParsedInQuery.rooms),
    startDate: sanitizeDateToISO(startDate, l10n, localDateFormat),
  };
};

const sanitizeDateToISO = (date: string, l10n: ILocalization, localDateFormat: string): string => {
  if (date) {
    let dateObj: Date = l10n.parseLocalDateString(date, { raw: localDateFormat }) as Date;

    if (!dateObj) {
      dateObj = l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;
    }

    const isoDateString = l10n.formatDate(dateObj, { raw: 'yyyy-MM-dd' });

    if (isoDateString) {
      return isoDateString;
    }
  }

  return '';
};

const localizeDateFromISO = (date: string, l10n: ILocalization, localDateFormat: string): string => {
  if (date) {
    const dateObj: Date = l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;
    const localDate = l10n.formatDate(dateObj, { raw: localDateFormat });

    if (localDate) {
      return localDate;
    }
  }

  return '';
};

const setDefaultRoomsIfNecessary = (rooms?: RoomModel[]) => {
  if (!rooms || !isArrayLike(rooms) || rooms.length === 0) {
    return [{ adults: 2, children: [] }];
  }

  return rooms;
};

interface CriteriaTypePair {
  key: string;
  type: string;
}

function getFilterKeys(): CriteriaTypePair[] {
  return [
    {
      key: 'sort',
      type: 'string',
    },
    {
      key: 'hotelName',
      type: 'string',
    },
    {
      key: 'price',
      type: 'array',
    },
    {
      key: 'star',
      type: 'array',
    },
    {
      key: 'lodging',
      type: 'array',
    },
    {
      key: 'amenities',
      type: 'array',
    },
    {
      key: 'travelerType',
      type: 'array',
    },
    {
      key: 'poi',
      type: 'string',
    },
    {
      key: 'guestRating',
      type: 'string',
    },
    {
      key: 'accessibility',
      type: 'array',
    },
    {
      key: 'paymentType',
      type: 'array',
    },
    {
      key: 'bedType',
      type: 'array',
    },
    {
      key: 'cleaningAndSafetyPractices',
      type: 'array',
    },
    {
      key: 'bedroomFilter',
      type: 'array',
    },
    {
      key: 'deals',
      type: 'array',
    },
    {
      key: 'propertyStyle',
      type: 'array',
    },
    {
      key: 'rewards',
      type: 'array',
    },
    {
      key: 'commissionTier',
      type: 'array',
    },
    {
      key: 'agencyBusinessModel',
      type: 'array',
    },
    {
      key: 'mealPlan',
      type: 'array',
    },
    {
      key: 'paandi',
      type: 'string',
    },
    {
      key: 'neighborhood',
      type: 'string',
    },
    {
      key: 'stayType',
      type: 'array',
    },
    {
      key: 'bedroom_count_gt',
      type: 'string',
    },
    {
      key: 'us_bathroom_count_gt',
      type: 'string',
    },
    {
      key: 'property_type_group',
      type: 'array',
    },
    {
      key: 'location_group',
      type: 'array',
    },
    {
      key: 'accessibility_features_group',
      type: 'array',
    },
    {
      key: 'booking_options_group',
      type: 'array',
    },
    {
      key: 'discounts_group',
      type: 'array',
    },
    {
      key: 'amenities_facilities_group',
      type: 'array',
    },
    {
      key: 'free_cancellation_group',
      type: 'array',
    },
    {
      key: 'house_rules_group',
      type: 'array',
    },
    {
      key: 'nearby_activities_group',
      type: 'array',
    },
    {
      key: 'neighborhood_group',
      type: 'array',
    },
    {
      key: 'premier_host_group',
      type: 'array',
    },
    {
      key: 'properties_good_for_group',
      type: 'array',
    },
    {
      key: 'property_reviews_group',
      type: 'array',
    },
    {
      key: 'rooms_spaces_group',
      type: 'array',
    },
    {
      key: 'safety_group',
      type: 'array',
    },
    {
      key: 'virtual_tour_group',
      type: 'array',
    },
    {
      key: 'nightly_price',
      type: 'array',
    },
    {
      key: 'total_price',
      type: 'array',
    },
  ];
}

function getFilterDefaults(): SerializedData {
  return {
    accessibility: [],
    amenities: [],
    guestRating: '',
    hotelName: '',
    lodging: [],
    poi: '',
    price: [],
    sort: PropertySort.RECOMMENDED,
    star: [],
    travelerType: [],
    paymentType: [],
    bedType: [],
    cleaningAndSafetyPractices: [],
    bedroomFilter: [],
    deals: [],
    propertyStyle: [],
    rewards: [],
    commissionTier: [],
    agencyBusinessModel: [],
    mealPlan: [],
    directFlights: 'false',
    neighborhood: '',
    stayType: [],
    bedroom_count_gt: '',
    us_bathroom_count_gt: '',
    property_type_group: [],
    location_group: [],
    accessibility_features_group: [],
    booking_options_group: [],
    discounts_group: [],
    amenities_facilities_group: [],
    free_cancellation_group: [],
    house_rules_group: [],
    nearby_activities_group: [],
    neighborhood_group: [],
    premier_host_group: [],
    properties_good_for_group: [],
    property_reviews_group: [],
    rooms_spaces_group: [],
    safety_group: [],
    virtual_tour_group: [],
    nightly_price: [],
    total_price: [],
  };
}

function ensureCorrectTypes(query: SerializedData): SerializedData {
  const correctedQuery = { ...query };
  const filterDefaults = getFilterDefaults();
  // iterate through the filters setting them to a parsed value or their default
  getFilterKeys().forEach(({ key, type }) => {
    if (key in correctedQuery) {
      // if it's a string but it's supposed to be an array
      if (type === 'array' && typeof correctedQuery[key] === 'string') {
        // parse based on comma separator
        correctedQuery[key] = correctedQuery[key].split(',').filter(val => val !== '');
      }
      // if it's a array but it's supposed to be an string
      if (type === 'string' && Array.isArray(correctedQuery[key])) {
        correctedQuery[key] = correctedQuery[key].join();
      }
    } else {
      correctedQuery[key] = filterDefaults[key];
    }
  });

  return correctedQuery;
}

function hasSearchCriteriaChanged(prevSearchCriteria: SearchCriteriaModel, newSearchCriteria: SearchCriteriaModel, compareKeys: string[]) {
  const prevSearchParams = merge.withKeys(compareKeys).merge({}, prevSearchCriteria.toJSON());
  const newSearchParams = merge.withKeys(compareKeys).merge({}, newSearchCriteria.toJSON());

  return !comparer.structural(prevSearchParams, newSearchParams);
}
export const SearchCriteriaUtils = {
  getFilterDefaults,
  ensureCorrectTypes,
  getToday,
  getTomorrow,
  localizeDateFromISO,
  parseInfositeValues,
  sanitizeDateToISO,
  hasSearchCriteriaChanged,
};
